/**
 * Conversion Architect API Service
 *
 * Follows the platform convention: every request uses the centralized
 * Axios client from `api.ts`, which automatically includes auth headers,
 * AI model selection, and tenant context.
 */

import api from '../api';

const BASE = '/api/conversion-architect';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ConversionProject {
  id: string;
  name: string;
  workstream: string;
  status: 'draft' | 'mapping' | 'validating' | 'complete';
  source_schema_id: string | null;
  target_schema_id: string | null;
  config: Record<string, any>;
  summary_stats: SummaryStats | null;
  created_at: string;
  updated_at: string;
}

export interface SummaryStats {
  total_rules: number;
  by_type: Record<string, number>;
  by_status: Record<string, number>;
  by_confidence: Record<string, number>;
  source_coverage: number;
  target_coverage: number;
  /** Count of DIRECT rules still in `proposed` status, project-wide. */
  direct_proposed?: number;
  questions_total?: number;
  questions_open?: number;
  questions_answered?: number;
  overall_health: string;
}

export interface SchemaSnapshot {
  id: string;
  role: 'source' | 'target';
  label: string;
  entity_count: number;
  column_mapping: Record<string, string>;
  profile_summary: Record<string, any>;
  ingested_at: string;
}

export interface AccountEntity {
  id: string;
  account_code: string;
  account_name: string;
  account_type: string;
  segment_values: Record<string, string>;
  hierarchy_path: string;
  hierarchy_depth: number;
  parent_account_code: string | null;
  attributes: Record<string, any>;
  is_active: boolean;
}

export interface MappingRule {
  id: string;
  source_entity_id: string;
  mapping_type: 'DIRECT' | 'SPLIT' | 'CONSOLIDATE' | 'ENRICH' | 'REBOOK' | 'UNMAPPED';
  confidence: 'high' | 'medium' | 'low' | 'none';
  confidence_score: number;
  status: 'proposed' | 'confirmed' | 'rejected' | 'pending_question';
  rationale: string;
  targets: MappingTarget[];
  /** Sequelize association alias — serialized camelCase by the server. */
  sourceEntity?: AccountEntity;
  question?: ConversionQuestion;
}

export interface MappingTarget {
  id: string;
  target_entity_id: string;
  allocation_method: string | null;
  allocation_percentage: number | null;
  attribute_overrides: Record<string, any>;
  /** Sequelize association alias — serialized camelCase by the server. */
  targetEntity?: AccountEntity;
}

export interface ConversionQuestion {
  id: string;
  question_type: string;
  severity: 'critical' | 'high' | 'medium';
  status: 'open' | 'answered' | 'auto_resolved' | 'dismissed';
  title: string;
  description: string;
  context: Record<string, any>;
  options: QuestionOption[];
  answer_type: string;
  mapping_rule_id: string | null;
  created_at: string;
  answer?: Record<string, any> | null;
  answered_by?: string | null;
  answered_at?: string | null;
}

export interface QuestionOption {
  label: string;
  preview_statement: string;
  resolution_function: string;
  sub_inputs?: SubInput[];
}

export interface SubInput {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'json';
  required?: boolean;
  options?: string[];
  placeholder?: string;
}

export interface StructuralComparison {
  segment_comparison: SegmentComparison[];
  hierarchy_comparison: HierarchyComparison;
  attribute_gap: AttributeGap[];
  account_type_alignment: AccountTypeAlignment[];
  candidate_alignments: CandidateAlignment[];
  summary: ComparisonSummary;
}

export interface SegmentComparison {
  target_segment: string;
  source_segment: string | null;
  match_type: 'exact' | 'fuzzy' | 'none';
  /** 0–1 share of target segment values also present in the source segment. */
  value_overlap: number;
  target_distinct_values: number;
  source_distinct_values: number;
  /** Target segment values with no counterpart in the matched source segment. */
  gap_values: string[];
}

export interface HierarchyComparison {
  source_max_depth: number;
  target_max_depth: number;
  depth_delta: number;
  source_leaf_count: number;
  target_leaf_count: number;
  source_total_count: number;
  target_total_count: number;
  /** Keyed by depth level: account counts at that level in each chart. */
  breadth_by_level: Record<string, { source: number; target: number }>;
}

export interface AttributeGap {
  attribute: string;
  present_in_target: boolean;
  present_in_source: boolean;
  /** 0–1 share of target accounts with this attribute populated. */
  target_fill_rate: number;
  target_distinct_values: number;
  requires_population_rule?: boolean;
}

export interface AccountTypeAlignment {
  account_type: string;
  source_count: number;
  target_count: number;
  delta: number;
}

export interface CandidateAlignment {
  source_id: string;
  target_id: string;
  source_code: string;
  target_code: string;
  match_method: 'exact_code' | 'exact_name' | 'fuzzy_name';
  similarity_score: number;
  account_type: string;
}

export interface ComparisonSummary {
  source_account_count: number;
  target_account_count: number;
  segment_count_source: number;
  segment_count_target: number;
  segments_matched: number;
  segments_unmatched: number;
  attributes_in_target_only: number;
  hierarchy_depth_delta: number;
  preliminary_alignment_count: number;
}

export interface ValidationResult {
  id: string;
  computed_at: string;
  source_coverage: {
    total: number;
    mapped: number;
    unmapped: number;
    coverage_rate: number;
    health: string;
    unmapped_accounts: Array<{ code: string; name: string }>;
  };
  target_coverage: {
    total: number;
    mapped_from_source: number;
    no_source_mapping: number;
    coverage_rate: number;
    health: string;
  };
  dollar_reconciliation: {
    status?: string;
    reason?: string;
    source_total?: number;
    target_total?: number;
    variance?: number;
    variance_pct?: number;
    reconciled?: boolean;
    health: string;
  };
  posting_rule_consistency: {
    total_posting_rules_checked: number;
    orphan_legs: number;
    health: string;
  };
  attribute_completeness: {
    total_required_attributes: number;
    populated: number;
    gaps: number;
    completeness_rate: number;
    health: string;
  };
  entity_coverage: {
    entities_in_scope: number;
    entities_with_issues: number;
    health: string;
  };
  overall_health: {
    status: string;
    checks_passed: number;
    checks_warning: number;
    checks_failed: number;
  };
  findings: Array<{
    check: string;
    severity: string;
    message: string;
    details?: any;
  }>;
}

// ---------------------------------------------------------------------------
// API Client Functions
// ---------------------------------------------------------------------------

// --- Phase 1: Project + Schema Setup ---

export async function createProject(data: {
  name: string;
  workstream: string;
  engagement_id?: string;
  config?: Record<string, any>;
}): Promise<ConversionProject> {
  const res = await api.post(`${BASE}/projects`, data);
  return res.data.data;
}

export async function listProjects(): Promise<ConversionProject[]> {
  const res = await api.get(`${BASE}/projects`);
  return res.data.data;
}

export async function getProject(projectId: string): Promise<ConversionProject> {
  const res = await api.get(`${BASE}/projects/${projectId}`);
  return res.data.data;
}

export interface ColumnDetection {
  detected?: Record<string, string>;
  unmatched_columns?: string[];
  [key: string]: any;
}

export interface UploadSchemaResult {
  schema: SchemaSnapshot;
  entity_count: number;
  column_detection: ColumnDetection;
  needs_confirmation: boolean;
  question?: ConversionQuestion;
}

export async function uploadSchema(
  projectId: string,
  role: 'source' | 'target',
  file: File,
  label?: string
): Promise<UploadSchemaResult> {
  const formData = new FormData();
  formData.append('file', file);
  if (label) formData.append('label', label);

  const res = await api.post(
    `${BASE}/projects/${projectId}/upload/${role}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return res.data.data;
}

export async function getSchema(
  projectId: string,
  role: 'source' | 'target'
): Promise<{ schema: SchemaSnapshot; entities: AccountEntity[] }> {
  const res = await api.get(`${BASE}/projects/${projectId}/schemas/${role}`);
  return res.data.data;
}

// --- Phase 2: Structural Comparison ---

export async function runComparison(projectId: string): Promise<StructuralComparison> {
  const res = await api.post(`${BASE}/projects/${projectId}/compare`);
  return res.data.data;
}

export async function getComparison(projectId: string): Promise<StructuralComparison> {
  const res = await api.get(`${BASE}/projects/${projectId}/comparison`);
  return res.data.data;
}

// --- Phase 3: Mapping Rules ---

export async function generateMappings(projectId: string): Promise<{
  rules_generated: number;
  questions_generated: number;
  summary: SummaryStats;
}> {
  const res = await api.post(`${BASE}/projects/${projectId}/generate-mappings`);
  return res.data.data;
}

export async function getMappings(
  projectId: string,
  filters?: {
    mapping_type?: string;
    status?: string;
    confidence?: string;
    page?: number;
    limit?: number;
  }
): Promise<{ rules: MappingRule[]; total: number; page: number; limit: number; pages: number }> {
  const res = await api.get(`${BASE}/projects/${projectId}/mappings`, { params: filters });
  // Server contract: { success, data: MappingRule[], pagination: { page, limit, total, pages } }
  const rules: MappingRule[] = res.data.data || [];
  const pagination = res.data.pagination || {};
  return {
    rules,
    total: pagination.total ?? rules.length,
    page: pagination.page ?? filters?.page ?? 1,
    limit: pagination.limit ?? filters?.limit ?? rules.length,
    pages: pagination.pages ?? 1,
  };
}

export async function getMappingDetail(
  projectId: string,
  ruleId: string
): Promise<MappingRule> {
  const res = await api.get(`${BASE}/projects/${projectId}/mappings/${ruleId}`);
  return res.data.data;
}

export async function updateMapping(
  projectId: string,
  ruleId: string,
  updates: Partial<Pick<MappingRule, 'status' | 'rationale'>> & {
    targets?: Array<{
      target_entity_id: string;
      allocation_method?: string;
      allocation_percentage?: number;
      attribute_overrides?: Record<string, any>;
    }>;
  }
): Promise<MappingRule> {
  const res = await api.patch(`${BASE}/projects/${projectId}/mappings/${ruleId}`, updates);
  return res.data.data;
}

export async function bulkConfirmMappings(
  projectId: string,
  options: { rule_ids?: string[] } = {}
): Promise<{ confirmed: number }> {
  // When no rule_ids are supplied the body is `{}` and the server's threshold
  // path confirms every proposed DIRECT rule project-wide.
  const body: { rule_ids?: string[] } = {};
  if (options.rule_ids && options.rule_ids.length > 0) {
    body.rule_ids = options.rule_ids;
  }
  const res = await api.post(`${BASE}/projects/${projectId}/mappings/bulk-confirm`, body);
  return res.data.data;
}

// --- Phase 4: Question Resolution ---

export async function getQuestions(
  projectId: string,
  filters?: { status?: string; severity?: string; sort?: string }
): Promise<ConversionQuestion[]> {
  const res = await api.get(`${BASE}/projects/${projectId}/questions`, { params: filters });
  return res.data.data;
}

export async function applyAnswer(
  projectId: string,
  questionId: string,
  answer: {
    selected_option: string;
    answer_data: Record<string, any>;
    preview_statement?: string;
  }
): Promise<{
  answer: Record<string, any>;
  question_status: string;
  preview_statement: string;
}> {
  const res = await api.post(
    `${BASE}/projects/${projectId}/questions/${questionId}/apply`,
    answer
  );
  return res.data.data;
}

// --- Phase 5: Validation ---

export async function runValidation(projectId: string): Promise<ValidationResult> {
  const res = await api.post(`${BASE}/projects/${projectId}/validate`);
  return res.data.data;
}

export async function getValidation(projectId: string): Promise<ValidationResult> {
  const res = await api.get(`${BASE}/projects/${projectId}/validation`);
  return res.data.data;
}

// --- Phase 6: Document Generation ---

export async function exportMappingTable(projectId: string): Promise<Blob> {
  const res = await api.post(
    `${BASE}/projects/${projectId}/export/mapping-table`,
    {},
    { responseType: 'blob' }
  );
  return res.data;
}

export async function exportBRD(projectId: string): Promise<{
  content: string;
  sections: Array<string | { id?: string; title?: string }>;
  generated_at: string;
}> {
  const res = await api.post(`${BASE}/projects/${projectId}/export/brd`);
  return res.data.data;
}

export async function exportFRD(projectId: string): Promise<{
  content: string;
}> {
  const res = await api.post(`${BASE}/projects/${projectId}/export/frd`);
  return res.data.data;
}

// ---------------------------------------------------------------------------
// Utility: Download blob as file
// ---------------------------------------------------------------------------

export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
